import {memo, useCallback, useEffect, useLayoutEffect, useRef} from 'react'
import type {ReceivedChatMessage} from '@livekit/components-react'
import {useLocalParticipant} from '@livekit/components-react'
import {ChatMessage} from './ChatMessage'

interface ChatMessageListProps {
  messages: ReceivedChatMessage[]
}

const BOTTOM_THRESHOLD_PX = 80

export const ChatMessageList = memo(function ChatMessageList({messages}: ChatMessageListProps) {
  const {localParticipant} = useLocalParticipant()
  const viewportRef = useRef<HTMLDivElement>(null)

  /**
   * isAtBottomRef: true = user at bottom, auto-scroll new messages; false = user read history, don't scroll.
   * During programmatic smooth scroll we ignore "not at bottom" from intermediate events (isProgrammaticScrollRef).
   */
  const isAtBottomRef = useRef(true)
  const isProgrammaticScrollRef = useRef(false)
  const programmaticScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevLengthRef = useRef(messages.length)

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = viewportRef.current
    if (!el) return
    if (smooth) {
      isProgrammaticScrollRef.current = true
      if (programmaticScrollTimeoutRef.current) {
        clearTimeout(programmaticScrollTimeoutRef.current)
      }
      programmaticScrollTimeoutRef.current = setTimeout(() => {
        programmaticScrollTimeoutRef.current = null
        isProgrammaticScrollRef.current = false
        isAtBottomRef.current = true
      }, 600)
    } else {
      isAtBottomRef.current = true
    }
    el.scrollTo({top: el.scrollHeight, behavior: smooth ? 'smooth' : 'instant'})
  }, [])

  const handleScroll = useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const atBottom = distanceFromBottom <= BOTTOM_THRESHOLD_PX

    if (isProgrammaticScrollRef.current) {
      if (atBottom) {
        isProgrammaticScrollRef.current = false
        if (programmaticScrollTimeoutRef.current) {
          clearTimeout(programmaticScrollTimeoutRef.current)
          programmaticScrollTimeoutRef.current = null
        }
      }
      isAtBottomRef.current = atBottom
    } else {
      isAtBottomRef.current = atBottom
    }
  }, [])

  // Scroll to bottom when the list first mounts (chat opened with existing messages)
  useLayoutEffect(() => {
    scrollToBottom(false)
    return () => {
      if (programmaticScrollTimeoutRef.current) {
        clearTimeout(programmaticScrollTimeoutRef.current)
      }
    }
  }, [scrollToBottom])

  // Auto-scroll when new messages arrive
  useEffect(() => {
    const newCount = messages.length - prevLengthRef.current
    prevLengthRef.current = messages.length

    if (newCount <= 0 || messages.length === 0) return

    const lastMsg = messages[messages.length - 1]
    const ownIdentity = localParticipant?.identity
    const isOwnMessage = Boolean(ownIdentity && lastMsg?.from?.identity === ownIdentity)

    if (isAtBottomRef.current || isOwnMessage) {
      scrollToBottom(true)
    }
  }, [messages, localParticipant?.identity, scrollToBottom])

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm px-4 text-center">
        Сообщений пока нет. Напишите первым!
      </div>
    )
  }

  return (
    <div
      ref={viewportRef}
      onScroll={handleScroll}
      className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
    >
      <div className="flex flex-col gap-0.5 py-2">
        {messages.map(msg => (
          <ChatMessage
            key={msg.id}
            message={msg}
            isOwn={msg.from?.identity === localParticipant.identity}
          />
        ))}
      </div>
    </div>
  )
})
